import React from 'react';
import { motion } from 'framer-motion';
import { WrenchIcon, CpuIcon } from 'lucide-react';
import type { SensorDetailSidebarProps } from './SensorDetailSidebar';
import { SensorHeader } from './SensorDetailSidebar';
import { useTranslation } from 'react-i18next';

export function ETCDetail({
  sensor,
  onBack,
  onClose,
  onUpdateSensor
}: SensorDetailSidebarProps) {
  const { t } = useTranslation();

  return (
    <div className="w-full flex flex-col gap-4 p-4  bg-[#0a0f1a]/40 h-full overflow-y-auto">
      {/* Sensor Header (Back, Close, Rename, Favorite) */}
      <SensorHeader
        sensor={sensor}
        subtitle={sensor.type ? `${sensor.type} 장치` : '스마트 기기'}
        onBack={onBack}
        onClose={onClose}
        onUpdateSensor={onUpdateSensor}
      />

      {/* Main Implementation Notice Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="glass-panel rounded-3xl p-8 flex flex-col items-center justify-center text-center my-auto border border-white/10 shadow-2xl bg-gradient-to-b from-slate-900/80 to-slate-950/90"
      >
        {/* Animated Icon Badge */}
        <div className="relative mb-5">
          <div className="w-20 h-20 rounded-3xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shadow-lg shadow-cyan-500/10">
            <CpuIcon className="w-10 h-10 animate-pulse" />
          </div>
          <div className="absolute -top-1.5 -right-1.5 p-1.5 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400">
            <WrenchIcon className="w-4 h-4" />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-base font-bold text-white mb-2 tracking-tight">
          상세정보 페이지를 구현중입니다.
        </h3>

        {/* Subtitle */}
        <p className="text-xs text-slate-400 max-w-xs leading-relaxed mb-6">
          <span className="text-cyan-400 font-semibold">{sensor.label || sensor.id}</span> ({sensor.type || '기타'}) 장치의 전용 상세 관제 및 제어 화면이 준비 중입니다.
        </p>

        {/* Device Quick Info Box */}
        <div className="w-full max-w-xs rounded-2xl bg-slate-950/60 border border-white/5 p-4 text-left space-y-2.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500 font-medium">장치 식별자</span>
            <span className="font-mono text-slate-300 font-semibold">{sensor.id}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500 font-medium">장치 유형</span>
            <span className="px-2 py-0.5 rounded-md bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 font-bold text-[0.7rem]">
              {sensor.type || 'ETC'}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500 font-medium">통신 상태</span>
            <span className={`flex items-center gap-1.5 font-semibold text-[0.72rem] ${sensor.active !== false ? 'text-emerald-400' : 'text-slate-400'}`}>
              <span className={`w-2 h-2 rounded-full ${sensor.active !== false ? 'bg-emerald-400' : 'bg-slate-500'}`} />
              {sensor.active !== false ? '온라인 연결됨' : '오프라인'}
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
